import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useCompanyName } from "@/lib/company";
import { ENGINE_LABELS } from "@/config";
import { Page, PageHeader, StatStrip, Segmented, Label11, PrivacyNote, Loading, usd, int, plural } from "@/components/common";
import { EngineMark, EngineWithLogo } from "@/components/ProviderLogo";
import { cn } from "@/lib/utils";

// The founder's Usage page (backend: project_usage.py): what Claude Agent and Codex used on each project in the last 7
// or 30 days, as a usage meter shows it. A row per project with its tokens as one bar; opening it shows the split by
// engine and model, its tool calls by kind, its MCP servers, cache expiries and the subagents' share of the tool calls.

// the four kinds of tokens, in the bar's order; colours: --gr-tok-* in index.css (checked for colour blindness, with
// their own steps in dark mode)
const TOKENS = [
  ["input", "Input", "var(--gr-tok-input)"],
  ["output", "Output", "var(--gr-tok-output)"],
  ["cache_write", "Cache write", "var(--gr-tok-cache-write)"],
  ["cache_read", "Cache read", "var(--gr-tok-cache-read)"],
];
const TOOL_KINDS = { read: "Read", bash: "Bash", edit: "Edit", search: "Search", mcp: "MCP", agent: "Agents", web: "Web", other: "Other" };

// 794K, 6.6M, 261.3M
const compact = (n) => new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);
// whole dollars from $100 up
const big = (n) => usd(n, Math.abs(n) >= 100 ? 0 : 2);
const share = (n, total) => (!total || !n ? "0%" : n / total < 0.01 ? "<1%" : `${Math.round((n / total) * 100)}%`);
const engineName = (e) => (ENGINE_LABELS[e] || e).replace(/ \(.*\)$/, "");

function Swatch({ color }) {
  return <span className="w-2 h-2 rounded-[2px] shrink-0" style={{ background: color }} aria-hidden="true" />;
}

// A project's tokens as one bar, a segment per kind with 2px between them; hovering a segment names it
function TokenBar({ tokens }) {
  const parts = TOKENS.filter(([k]) => tokens[k] > 0);
  const label = parts.map(([k, name]) => `${name} ${compact(tokens[k])}`).join(", ");
  return (
    <span role="img" aria-label={`Tokens: ${label}`} className="flex flex-1 min-w-0 gap-[2px]">
      {parts.map(([k, name, color]) => (
        <span key={k} className="group relative py-1" style={{ flex: `${tokens[k]} 1 0%`, minWidth: 3 }}>
          <span className="block h-2 rounded-[2px] group-first:rounded-l-[4px] group-last:rounded-r-[4px]" style={{ background: color }} />
          <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-popover px-2 py-1 text-xs text-foreground shadow-[var(--gr-shadow-menu)] group-hover:block">
            {name} · {compact(tokens[k])} · {share(tokens[k], tokens.total)}
          </span>
        </span>
      ))}
    </span>
  );
}

function Legend() {
  return (
    <ul className="m-0 p-0 list-none flex flex-wrap gap-x-4 gap-y-1 text-sm text-gr-ink-secondary" aria-label="Kinds of tokens">
      {TOKENS.map(([k, name, color]) => <li key={k} className="flex items-center gap-1.5"><Swatch color={color} />{name}</li>)}
    </ul>
  );
}

// One block of a project's details: a label over rows of name, value and share
function Block({ title, children, testid }) {
  return (
    <div className="flex flex-col gap-2 min-w-0" data-testid={testid}>
      <Label11>{title}</Label11>
      {children}
    </div>
  );
}

function Rows({ rows, empty }) {
  if (!rows.length) return <span className="text-muted-foreground">{empty}</span>;
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-4 gap-y-1.5 tabular-nums">
      {rows.map(({ key, name, value, pct }) => (
        <div key={key} className="contents">
          <span className="flex items-center gap-2 min-w-0 truncate">{name}</span>
          <span className="text-end">{value}</span>
          <span className="text-end text-muted-foreground w-9">{pct}</span>
        </div>
      ))}
    </div>
  );
}

function Details({ p, days }) {
  const { data, isLoading } = useQuery({
    queryKey: ["usage", "project", p.id, days],
    queryFn: async () => (await api.get(`/usage/projects/${p.id}`, { params: { days } })).data,
  });
  const total = p.tokens.total;
  const waiting = <span className="text-muted-foreground">Loading…</span>;
  return (
    <div className="grid gap-x-8 gap-y-5 px-5 pt-1 pb-5 grid-cols-[repeat(auto-fit,minmax(220px,1fr))]" data-testid={`usage-details-${p.name}`}>
      <Block title="Tokens">
        <Rows rows={TOKENS.map(([k, name, color]) => ({ key: k, name: <><Swatch color={color} />{name}</>, value: compact(p.tokens[k]), pct: share(p.tokens[k], total) }))} />
      </Block>
      <Block title="Engines">
        <Rows rows={p.engines.map((e) => ({ key: e.engine, name: <EngineWithLogo engine={e.engine} />, value: usd(e.cost_usd), pct: share(e.cost_usd, p.cost_usd) }))} />
      </Block>
      <Block title="Models">
        <Rows rows={p.models.map((m) => ({
          key: `${m.engine}:${m.model}`, value: usd(m.cost_usd), pct: share(m.cost_usd, p.cost_usd),
          name: <><EngineMark engine={m.engine} className="w-3.5 h-3.5" /><span className="truncate" title={m.model}>{m.label}</span></>,
        }))} />
      </Block>
      <Block title="Tools (calls)" testid="usage-tools">
        {isLoading || !data ? waiting : (
          <Rows empty="No tool calls" rows={data.tools.map((t) => ({ key: t.kind, name: TOOL_KINDS[t.kind] || t.kind, value: int(t.calls), pct: share(t.calls, data.tool_calls) }))} />
        )}
      </Block>
      <Block title="MCP servers (calls)" testid="usage-mcp">
        {isLoading || !data ? waiting : (
          <Rows empty="None used" rows={data.mcp_servers.map((s) => ({ key: s.server, name: <span className="truncate">{s.server}</span>, value: int(s.calls), pct: "" }))} />
        )}
      </Block>
      <Block title="Cache and subagents" testid="usage-cache">
        {isLoading || !data ? waiting : (
          <div className="flex flex-col gap-1.5">
            <span>{data.cache.expired ? `Expired ${int(data.cache.expired)}× · ${compact(data.cache.rewritten_tokens)} tokens written again` : "No cache expiries"}</span>
            <span>{data.subagent_calls ? `Subagents: ${share(data.subagent_calls, data.tool_calls)} of the tool calls (${int(data.subagent_calls)})` : "No tool calls in subagents"}</span>
            <span className="text-sm text-muted-foreground [text-wrap:pretty]">A cache lasts 5 minutes after its last use (an hour when the engine asks for it). After a longer pause, the next request writes it again.</span>
          </div>
        )}
      </Block>
    </div>
  );
}

function ProjectRow({ p, days, open, onToggle }) {
  return (
    <div className="border-t first:border-t-0" data-testid={`usage-project-${p.name}`}>
      <button type="button" aria-expanded={open} onClick={onToggle}
        className="w-full text-start px-5 py-3.5 flex flex-col gap-1.5 hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring">
        <span className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate"><span className="font-semibold">{p.name}</span><span className="text-muted-foreground"> · {plural(p.sessions, "session")}</span></span>
          <span className="flex items-center gap-1.5 font-semibold tabular-nums">
            {usd(p.cost_usd)}<ChevronDown className={cn("w-4 h-4 transition-transform", open && "rotate-180")} aria-hidden="true" />
          </span>
        </span>
        <span className="flex items-center gap-3">
          <TokenBar tokens={p.tokens} />
          <span className="w-16 shrink-0 text-end text-sm text-muted-foreground tabular-nums">{compact(p.tokens.total)}</span>
        </span>
      </button>
      {open && <Details p={p} days={days} />}
    </div>
  );
}

export default function Usage() {
  const company = useCompanyName();
  const [params, setParams] = useSearchParams();
  const days = params.get("days") === "30" ? 30 : 7;
  const [open, setOpen] = useState(() => new Set());
  const { data, isLoading } = useQuery({
    queryKey: ["usage", days], refetchInterval: 60000,
    queryFn: async () => (await api.get("/usage", { params: { days } })).data,
  });
  const setDays = (d) => { const p = new URLSearchParams(params); if (d === 7) p.delete("days"); else p.set("days", d); setParams(p, { replace: true }); };
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const eyebrow = [company, "Usage"].filter(Boolean).join(" · ");
  const period = (
    <Segmented label="Period" value={days} onChange={setDays} testid="usage-days" options={[[7, "7 days"], [30, "30 days"]]} />
  );
  if (isLoading || !data) return <Page><PageHeader eyebrow={eyebrow} title="What the engines used on each project." actions={period} /><Loading /></Page>;

  const { totals, projects } = data;
  const cacheRead = projects.reduce((a, p) => a + p.tokens.cache_read, 0);
  return (
    <Page data-testid="owner-usage">
      <PageHeader eyebrow={eyebrow} title="What the engines used on each project." actions={period}
        status={projects.length ? `${big(totals.cost_usd)} on ${plural(projects.length, "project")} in the last ${days} days.`
          : `No Claude Agent or Codex requests in the last ${days} days.`} />
      {projects.length > 0 && (
        <StatStrip min={160} cellClassName="px-5 py-3.5 gap-0.5" items={[
          { label: "Spent", value: big(totals.cost_usd), big: true, sub: `Across ${plural(totals.sessions, "session")}` },
          { label: "Tokens", value: compact(totals.tokens), big: true, sub: `${share(cacheRead, totals.tokens)} read from the cache` },
          ...data.engines.map((e) => ({ label: engineName(e), value: big(totals.engines[e] || 0), big: true, sub: share(totals.engines[e], totals.cost_usd) + " of the spend" })),
        ]} />
      )}
      {projects.length > 0 && (
        <section aria-label="Projects" className="bg-card rounded-[10px]">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 py-3 border-b">
            <h2 className="m-0 font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold">Projects</h2>
            <Legend />
          </div>
          {projects.map((p) => <ProjectRow key={p.id} p={p} days={days} open={open.has(p.id)} onToggle={() => toggle(p.id)} />)}
        </section>
      )}
      <PrivacyNote>
        Claude Agent and Codex requests only, as the metering proxy counted them: tokens, money and tool names, never prompts, replies or what a tool did. Spend is what you paid, as your budgets count it.
      </PrivacyNote>
    </Page>
  );
}
